<template xmlns:v-slot="http://www.w3.org/1999/XSL/Transform">
    <div
        class="col-sm-12 invoice-container"
    >
        <div class="d-flex justify-content-between align-items-center mb-2">
            <h5 class="ml-2 mt-1 mb-1"><b>{{ contractName }}</b></h5>
            <invoice-attachment-selector
                v-if="invoice"
                v-model="selectedAttachments"
                :contract-attachments="contractAttachments"
                :contract-id="contractId"
                @downloadAttachment="downloadAttachment"
            />
        </div>
        <div class="row px-2 mt-3">
            <div class="col-2 header-title"> {{ $t('orders.contractor') }}</div>
            <div class="col" style="font-size: .9em">{{ contractorName }}</div>
        </div>
        <div class="row px-2">
            <div class="col-2 header-title"> {{ $t('contracts.number') }}</div>
            <div class="col" style="font-size: .9em">{{ contractNumber }}</div>
        </div>
        <div class="row px-2">
            <div class="col-2 header-title"> {{ $t('contracts.worksite_key') }}</div>
            <div class="col" style="font-size: .9em">{{ contractWorkKey }}</div>
        </div>
        <!-- Header Row -->
        <div class="col-12">
            <div class="row pt-4" style="font-size: .9em">
            <div class="col-3 single-line text-bold" style="padding-left: 1.3em">
                <span class="ps-4">{{ $t('common.close') }}</span>
                <span class="px-3">{{ $t('orders.order') }}</span>
            </div>
            <div class="col-2 single-line text-bold">
                <span>{{ $t('common.time_range') }}</span>
            </div>
                <div class="col-2 single-line text-bold pl-2">
                    <span>{{ $t('account.account') }}</span>
                </div>
                <div class="col-1 single-line text-bold pl-2">
                    <span>{{ $t('account.invoicing_code') }}</span>
                </div>
            <div class="col-1 single-line text-bold pl-1">
                <span>{{ $t('contracts.unit') }}</span>
            </div>
            <div class="col-1 single-line text-bold pl-2">
                <span>{{ $t('additional_cost.unit_price') }}</span>
            </div>
            <div class="col-1 single-line text-bold pl-2">
                <span>{{ $t('daily_summary.invoiceable_amount') }}</span>
            </div>
            <div class="col-1 single-line text-bold pl-2">
                <span>{{ $t('daily_summary.cost', {currency: currency}) }}</span>
            </div>
        </div>
        </div>
        <!-- Orders -->
        <invoicing-order-item
            v-for="order in invoice.invoice_rows"
            ref="orderItems"
            v-bind:key="order.id"
            :order="order"
            :show-all="false"
            :invoice-state-options="invoiceStateOptions"
            :user="user"
            :text-col-width-percentage="100"
            :is-selected="selectedOrders.includes(rowOrderId(order))"
            @showOrder="emitShowOrder"
            @showTripOrder="emitTripOrder"
            @showContractDetails="emitContractDetails"
            @showTripDetails="emitTripDetails"
            @showInvoiceHistories="emitInvoiceHistories"
            @updateSummaries="emitSummaries"
            @showTripDeliveries="emitTripDeliveries"
            @toggleSelection="toggleOrderSelection"
            @orderUpdated="$emit('orderUpdated')"
        />
        <!-- Total sum row -->
        <div class="row px-4 mb-2">
            <div class="col-10 m-0" style="padding-left: 4.3em; text-transform: uppercase; border-top: 1px solid #7FC000">{{ $t('common.total')}}</div>
            <div  class="col-2 m-0 d-flex justify-content-end" style="padding-right: 1.3em; text-transform: uppercase; border-top: 1px solid #7FC000">{{ totalCost.toFixed(2) }}</div>
        </div>
    </div>
    <div class="col-12 my-2 d-flex justify-content-end align-items-center">
        <span v-if="!hasContractSystem(user)" class="text-danger small me-3">{{ $t('invoice_history.invoicing_integration_required') }}</span>
        <span v-else-if="!invoice.contract.contract_number" class="text-danger small me-3">{{ $t('contracts.missing_contract_number') }}</span>
        <button
            class="btn btn-success form-button"
            :disabled="acceptDisabled"
            @click.stop="acceptInvoicing"
        >
            {{ $t('work_permission.accept') }}
        </button>
    </div>
</template>

<script>
import {workManagementHelper} from '../mixins/WorkManagementMixin'
import {priceHelper} from '@/components/mixins/PricingMixin'
import {timeUtils} from '../mixins/TimeUtils'
import {restApi} from '../mixins/RestApiMixin'
import {attachmentHelper} from '@/components/mixins/AttachmentMixin'
import {downloadHelper} from '../mixins/DownloadMixin'
import {userHelper} from '@/components/mixins/UserMixin'
import InvoicingOrderItem from "@/components/invoice/InvoicingOrderItem.vue";
import InvoiceAttachmentSelector from "@/components/invoice/InvoiceAttachmentSelector.vue";

export default {
    name: 'InvoicingContractTable',
    components: {InvoicingOrderItem, InvoiceAttachmentSelector},
    mixins: [timeUtils, workManagementHelper, priceHelper, restApi, attachmentHelper, downloadHelper, userHelper],
    props: {
        invoice: {
            type: Object,
            default: null
        },
        invoiceStateOptions: {
            type: Array,
            default() {
                return []
            }
        },
        user: {
            type: Object,
            default: null
        },
    },
    data() {
        return {
            sortBy: 'task_type',
            sortDesc: false,
            tableItems: [],
            loading: false,
            contractAttachments: [],
            selectedAttachments: [],
            selectedOrders: []
        }
    },
    mounted: function () {
        this.initTableItems()
        this.initAttachments()
    },
    watch: {
        invoice: function () {
            this.initTableItems()
            this.initAttachments()
        }
    },
    computed: {
        contractName() { return this.invoice?.contract?.name ?? '' },
        contractorName() { return this.invoice?.contract?.customer?.name ?? '' },
        contractNumber() { return this.invoice?.contract?.contract_number ?? '' },
        contractWorkKey() { return this.invoice?.contract?.work_site_key ?? '' },
        contractId() { return this.invoice?.contract?.id ?? null },
        totalCost: function () {
            let result = 0
            if (this.invoice?.invoice_rows?.length > 0) {
                this.invoice.invoice_rows.forEach(row => {
                    ;(row.items || []).forEach(item => { result += (item.invoiceable_sum == null || item.invoiceable_sum === 0) ? (item.cost ?? 0) : item.invoiceable_sum })
                })
            }
            return result
        },
        currency() {
            return import.meta.env.VITE_APP_CURRENCY || '€'
        },
        acceptDisabled() {
            if (!this.hasContractSystem(this.user)) return true
            if (this.invoice?.invoice_rows?.length > 0) {
                if (!this.invoice.contract.contract_number) return true
                return this.invoice.invoice_rows.some(row =>
                    (row.items || []).some(item => !item.order?.account || !item.order?.invoicing_code)
                )
            }
            return true
        },
        fields() {
            return [
                {field: 'task_type', title: this.$t('orders.task_type'), sort: false, headerClass: 'routa-table-header'},
                {field: 'executors', title: this.$t('orders.executor'), sort: false, headerClass: 'routa-table-header'},
                {field: 'time', title: this.$t('areas.time'), sort: false, headerClass: 'routa-table-header'},
                {field: 'info', title: this.$t('common.info'), sort: false, headerClass: 'routa-table-header'},
                {field: 'amount', title: this.$t('contracts.amount'), sort: false, headerClass: 'routa-table-header'},
                {field: 'pricing_model', title: this.$t('contracts.unit'), sort: false, headerClass: 'routa-table-header'},
                {field: 'unit_price', title: this.$t('additional_cost.unit_price'), sort: false, headerClass: 'routa-table-header'},
                {field: 'cost', title: this.$t('daily_summary.cost', {currency: this.currency}), sort: false, headerClass: 'routa-table-header'},
            ]
        },
    },
    methods: {
        initTableItems() {
            this.tableItems = []
            if (this.invoice && this.invoice.invoice_rows && this.invoice.invoice_rows.length > 0) {
                this.invoice.invoice_rows.forEach(item => {
                    this.tableItems.push(item)
                })
            }
        },
        collapseOrderRows() {
            this.$nextTick(() => {
                let orders = this.$refs.orderItems || [];
                if (!Array.isArray(orders)) orders = [orders];

                orders.forEach((order, idx) => {
                    if (!order) return;
                    if (typeof order.canClose === 'function' && !order.canClose()) return;
                    setTimeout(() => {
                        if (order && typeof order.collapseRow === 'function') {
                            order.collapseRow();
                        }
                    }, idx * 80);
                });
            });
        },
        rowOrderId(row) {
            return row.items?.[0]?.order?.id ?? null
        },
        acceptInvoicing() {
            if (this.invoice?.invoice_rows?.length > 0) {
                const invoiceData = {
                    id: this.invoice.id,
                    created: this.invoice.created,
                    paid: this.invoice.paid ?? null,
                    status: this.invoice.status ?? null,
                    status_date: this.invoice.status_date ?? null,
                    total_sum: this.totalCost,
                    contract: this.invoice.contract,
                    invoice_rows: this.invoice.invoice_rows.map(row => ({
                        id: row.id ?? null,
                        items: (row.items || []).map(i => ({
                            ...i,
                            covered_road_distance: i.covered_road_distance === '' ? null : i.covered_road_distance
                        }))
                    })),
                    attachment_ids: this.selectedAttachments,
                    orders_to_close: this.selectedOrders,
                }
                this.$emit('acceptInvoicing', invoiceData)
            }
        },
        toggleOrderSelection(orderId) {
            const index = this.selectedOrders.indexOf(orderId)
            if (index > -1) {
                this.selectedOrders.splice(index, 1)
            } else {
                this.selectedOrders.push(orderId)
            }
        },
        emitShowOrder(order) {
            this.$emit('showOrder', order, this.invoice)
        },
        emitTripOrder(row) {
            this.$emit('showTripOrder', row)
        },
        emitContractDetails(row) {
            this.$emit('showContractDetails', row)
        },
        emitInvoiceHistories(row) {
            this.$emit('showInvoiceHistories', row)
        },
        emitTripDetails(row) {
            this.$emit('showTripDetails', row)
        },
        emitTripDeliveries(row) {
            this.$emit('showTripDeliveries', row)
        },
        emitSummaries() {
            this.$emit('updateSummaries')
        },
        initAttachments() {
            this.contractAttachments = this.invoice?.contract?.attachments ?? []
        },
        downloadAttachment(attachment) {
            this.loading = true
            this.restFetch(this.contractAttachmentUrl + this.contractId + '/' + attachment.id,
                response => {
                    const fileName = attachment.file_original_name || attachment.filename || 'download'
                    this.saveFile(fileName, response.data)
                    this.loading = false
                }, () => {
                    this.loading = false
                })
        }
    }
}
</script>

<style>
.header-title {
    font-weight: bold;
    font-size: .9em;
    color: #90b677;
    padding: 0;
    margin: 0;
    margin-right: 1em;
    padding-left: .5em;
}


.invoice-table td,
.invoice-table th {
    font-size: .9em;
    border: none;
    border-bottom: 1px solid #dee2e6;
}

.invoice-table thead th div {
    font-weight: bold;
}

.invoice-container {
    background: #FFFFFF;
    border-radius: .5em;
    border: 1px #d9d9d9 solid;
    padding: .5em;
    box-shadow: #d9d9d9 1px 1px 4px;
}

</style>
